import { slotOpen, todayIST } from "@templeos/shared";
import type { GateScan, PosDB } from "./db.js";

/**
 * Gate entry rules that must also work without the server (SOW Module 3): code normalising, offline whitelist
 * decisions with local anti-passback, the offline upload, and throughput.
 * "check" is the gate's own verdict for a code it cannot decide: an offline slip, or a code missing from the offline
 * list (sold after the list was taken). Staff check the receipt by hand; the turnstile stays closed.
 */
export type GateResult = "valid" | "used" | "invalid" | "void" | "expired" | "wrong_date" | "check";

/**
 * Scanners send the bare 14-character code (12 random + 2 check characters), the pass URL ending in it, or an offline
 * slip's sale reference with the ticket number (clientRef/n).
 */
export function normalizeScan(raw: string): string | null {
  const s = raw.trim().toUpperCase();
  if (isOfflineSlip(s)) return s;
  const m = /(?:^|[^A-Z0-9])([A-Z0-9]{14})\/?$/.exec(s);
  return m ? m[1]! : null;
}

/** A provisional code printed on an offline slip before the server issued the real ticket codes. */
export const isOfflineSlip = (code: string): boolean => /^[A-Z0-9-]{2,12}-\d{8}-\d{5}-[A-Z0-9]{6}\/\d{1,4}$/.test(code);

/**
 * Offline decision: a code let in today (here, or at another gate over the live link) is a re-entry attempt; a code on
 * today's whitelist is valid; anything else needs a check by hand, because the list is only as fresh as its last
 * download. Valid scans are uploaded later so the server records the use.
 */
export function offlineVerdict(code: string, whitelist: ReadonlySet<string>, usedToday: ReadonlySet<string>, whitelistDay: string | null, day = todayIST(), rules?: Pick<GateWhitelist, "slots" | "graceMinutes">, at = new Date()): GateResult {
  if (usedToday.has(code)) return "used";
  if (whitelistDay !== day) return "check";
  if (!whitelist.has(code) || !rules?.slots || !Object.hasOwn(rules.slots, code)
    || !Number.isInteger(rules.graceMinutes) || rules.graceMinutes! < 0) return "check";
  const slot = rules.slots[code];
  if (slot !== null && typeof slot !== "string") return "check";
  return slotOpen(slot, at, rules.graceMinutes!) ? "valid" : "wrong_date";
}

export interface GateWhitelist { day: string; codes: string[]; at: string; slots?: Record<string, string | null>; graceMinutes?: number }

/** Recheck after every awaited refresh/cache read, including requests spanning midnight. */
export function currentWhitelist(list: GateWhitelist | undefined, requestedDay: string, day = todayIST()): list is GateWhitelist {
  return Boolean(list && list.day === requestedDay && requestedDay === day);
}

export async function usedGateCodes(db: PosDB, day = todayIST()): Promise<Set<string>> {
  const rows = await db.scans.filter(r => r.verdict === "valid" && todayIST(new Date(r.scannedAt)) === day).toArray();
  return new Set(rows.map(r => r.code));
}

/** A remote admission is already on the server, but must survive this terminal restarting offline. */
export async function recordRemoteAdmission(db: PosDB, code: string, gateCode: string, at: string): Promise<void> {
  if (!Number.isFinite(Date.parse(at))) throw new Error("Remote admission is missing a valid timestamp.");
  await db.scans.add({ code, gateCode, scannedAt: at, verdict: "valid", synced: 1 });
}

/** Persist consumption before touching hardware. An ambiguous pulse never makes the ticket reusable. */
export async function admitGate(db: PosDB, scan: Omit<GateScan, "id" | "verdict" | "relay">, pulse: () => Promise<boolean | "simulated">, offlineDay?: string | null, offlineReady?: () => boolean): Promise<"opened" | "simulated" | "used" | "relay_failed" | "stale"> {
  if (!scan.synced && (offlineDay !== todayIST() || offlineReady?.() === false)) return "stale";
  const id = await db.transaction("rw", db.scans, async () => {
    if (!scan.synced) {
      const day = todayIST(new Date(scan.scannedAt));
      const used = await db.scans.where("code").equals(scan.code)
        .filter(r => r.verdict === "valid" && todayIST(new Date(r.scannedAt)) === day).first();
      if (used) return null;
    }
    return db.scans.add({ ...scan, verdict: "valid", relay: "pending" });
  });
  if (id === null) return "used";
  if (!scan.synced && (offlineDay !== todayIST() || offlineReady?.() === false)) return "stale";
  let opened: boolean | "simulated" = false;
  try { opened = await pulse(); } catch { /* ambiguous hardware outcome: keep consumption */ }
  await db.scans.update(id, { relay: opened === "simulated" ? "simulated" : opened ? "opened" : "uncertain" });
  if (opened === "simulated") return "simulated";
  return opened ? "opened" : "relay_failed";
}

export async function pulseGateRelay(url: string, gate: string): Promise<boolean | "simulated"> {
  try {
    const res = await fetch(`${url}/relay`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ gate, pulseMs: 800 }), signal: AbortSignal.timeout(1_500) });
    if (!res.ok) return false;
    const reply = await res.json() as { ok?: boolean; mode?: string };
    return reply.ok === true ? reply.mode === "simulator" ? "simulated" : reply.mode === "http" : false;
  } catch { return false; }
}

/** POST /api/counters/gate/scan. */
export interface ScanReply {
  result: GateResult;
  ticket?: { code: string; itemName: string; serviceDate: string | null; slot: string | null; usedAt?: string; usedGate?: string };
  message?: string;
}

/**
 * Where a scan is decided: by the server whenever it answers, offline slips included (it resolves "clientRef/n" to the
 * real ticket once the sale has synced, and answers "check" before). Only a dead link falls back to the offline list,
 * which never holds a slip, so a slip is never let in offline. Any other error is thrown to the caller.
 */
export async function gateVerdict(code: string, post: (code: string) => Promise<ScanReply>, linkDown: (e: unknown) => boolean,
  offline: () => GateResult): Promise<{ reply: ScanReply; offline: boolean }> {
  try {
    return { reply: await post(code), offline: false };
  } catch (e) {
    if (!linkDown(e)) throw e;
    return { reply: { result: offline() }, offline: true };
  }
}

export const RESULT_TEXT: Record<GateResult, string> = {
  valid: "Entry allowed",
  used: "Already used - re-entry not allowed",
  invalid: "Invalid or forged ticket",
  void: "Ticket cancelled",
  expired: "Ticket expired",
  wrong_date: "Ticket is for another day",
  check: "Check the receipt by hand",
};

/** Entries per minute over the last `windowMs`. */
export function perMinute(times: readonly number[], now: number, windowMs = 5 * 60_000): number {
  const recent = times.filter((t) => now - t <= windowMs).length;
  return Math.round((recent / (windowMs / 60_000)) * 10) / 10;
}

export interface UploadScan { code: string; gateCode: string; scannedAt: string; verdict?: string }
/** One result per scan; "error" means the server could not record it. */
export type ScanUploader = (scans: UploadScan[]) => Promise<{ results: { code: string; scannedAt: string; result: string }[] }>;
/**
 * entries: POST /api/counters/gate/sync, which marks the tickets used. refused: POST /api/counters/gate/refused, the
 * audit trail of scans refused offline, which never touches a ticket.
 */
export interface ScanUploaders { entries: ScanUploader; refused: ScanUploader }

const UPLOAD_CHUNK = 500; // the server takes at most 5000 per request
let uploading: Promise<number> | null = null;

/**
 * Upload offline entries and refusals in chunks. Only scans the server recorded are marked uploaded; a scan it answered
 * with an error stays queued for the next round, so the ticket is still marked used everywhere in the end. One upload
 * runs at a time (the timer and the "online" event must not send the same scans twice). Returns how many were recorded.
 */
export function uploadScans(db: PosDB, post: ScanUploaders, chunk = UPLOAD_CHUNK): Promise<number> {
  uploading ??= (async () => {
    try {
      let recorded = 0;
      for (;;) {
        const rows = await db.scans.where("synced").equals(0).limit(chunk).toArray();
        if (!rows.length) return recorded;
        const entries = rows.filter((r) => r.verdict === "valid");
        const refused = rows.filter((r) => r.verdict !== "valid");
        const results = [
          ...(entries.length ? (await post.entries(entries.map((r) => ({ code: r.code, gateCode: r.gateCode, scannedAt: r.scannedAt })))).results : []),
          ...(refused.length ? (await post.refused(refused.map((r) => ({ code: r.code, gateCode: r.gateCode, scannedAt: r.scannedAt, verdict: r.verdict })))).results : []),
        ];
        const done = new Set(results.filter((r) => r.result !== "error").map((r) => `${r.code}|${r.scannedAt}`));
        const accepted = rows.filter((r) => done.has(`${r.code}|${r.scannedAt}`));
        await db.scans.bulkUpdate(accepted.map((r) => ({ key: r.id!, changes: { synced: 1 as const } })));
        recorded += accepted.length;
        if (accepted.length < rows.length) return recorded; // the rest waits for the next round
      }
    } finally {
      uploading = null;
    }
  })();
  return uploading;
}
